import React, { useState } from 'react';
import { useStableDisclosure } from '../../utils/stableDisclosure';
import styled, { css } from 'styled-components';
import { shimmerText } from './activityShimmer';
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GavelIcon from '@mui/icons-material/Gavel';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ForumIcon from '@mui/icons-material/Forum';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ToolActionContentItem } from '../../shared/interfaces';

interface Props {
  data: ToolActionContentItem;
}

const TOOL_ICON: Record<ToolActionContentItem['tool'], React.ReactNode> = {
  web_search: <SearchIcon style={{ fontSize: 15 }} />,
  page_scraping: <LanguageIcon style={{ fontSize: 15 }} />,
  tool_discovery: <SearchIcon style={{ fontSize: 15 }} />,
  skill_read: <ReceiptLongIcon style={{ fontSize: 15 }} />,
  data_ocean_query: <StorageIcon style={{ fontSize: 15 }} />,
  erp_query: <ReceiptLongIcon style={{ fontSize: 15 }} />,
  erp_create_invoice: <ReceiptLongIcon style={{ fontSize: 15 }} />,
  datajud_query: <GavelIcon style={{ fontSize: 15 }} />,
  thinking: <PsychologyIcon style={{ fontSize: 15 }} />,
  buscar_conversas: <ForumIcon style={{ fontSize: 15 }} />,
};

// Label per tool: [pending phrase, done phrase]. The phrase is fixed
// (no query interpolation) — queries live in the expanded body.
export const TOOL_LABEL: Record<ToolActionContentItem['tool'], [string, string]> = {
  web_search:        ['Pesquisando na web',          'Buscou na web'],
  page_scraping:     ['Lendo página',                  'Leu página'],
  tool_discovery:   ['Buscando ferramentas', 'Ferramentas encontradas'],
  skill_read:       ['Lendo skill', 'Skill carregada'],
  data_ocean_query:  ['Consultando base de dados',   'Consultou base de dados'],
  erp_query:         ['Consultando ERP',             'Consultou ERP'],
  erp_create_invoice:['Emitindo nota',               'Emitiu nota'],
  datajud_query:     ['Consultando DataJud',         'Consultou DataJud'],
  thinking:          ['Pensando',                    'Pensou'],
  buscar_conversas:  ['Buscando nas conversas',      'Buscou nas conversas'],
};

// For data_ocean_query, the detail line should name *which* bases were
// consulted (dataset.table, project id stripped) — never the raw SQL, which
// would leak the full query and internal table layout. This also sanitizes
// chats that were persisted before the backend stopped sending the SQL.
function consultedBases(sql: string): string {
  if (!sql) return '';
  const seen = new Set<string>();
  const bases: string[] = [];
  const add = (ref: string) => {
    const parts = ref.split('.').filter(Boolean);
    if (parts.length < 2) return;
    const base = parts.slice(-2).join('.'); // dataset.table (drop project id)
    if (!seen.has(base)) { seen.add(base); bases.push(base); }
  };
  const backtick = sql.match(/`([^`]+)`/g) || [];
  backtick.forEach((m) => add(m.replace(/`/g, '')));
  if (bases.length === 0) {
    const m = sql.match(/(?:from|join)\s+`?([A-Za-z0-9_\-]+(?:\.[A-Za-z0-9_\-]+){1,2})`?/gi) || [];
    m.forEach((frag) => add(frag.replace(/^(?:from|join)\s+/i, '').replace(/`/g, '')));
  }
  if (bases.length === 0) {
    // Bare qualified table ref(s), e.g. legacy "maritica-1.eleicoes.candidatos"
    // persisted before the backend stripped the project id.
    sql.split(',').forEach((part) => {
      const tok = part.trim();
      if (/^`?[A-Za-z_][\w\-]*(?:\.[A-Za-z_][\w\-]*){1,2}`?$/.test(tok)) add(tok.replace(/`/g, ''));
    });
  }
  return bases.join(', ');
}

function sanitizeDataOceanLabel(q: string): string {
  const bases = consultedBases(q);
  if (bases) return bases;
  // Looks like SQL but no table parsed → hide it rather than leak the query.
  if (/\bselect\b/i.test(q) && /\bfrom\b/i.test(q)) return '';
  return q; // plain search term — safe to show
}

// Non-prod debug build flag (mirrors REACT_APP_ENABLE_COMPETITOR): when on, the
// expanded data_ocean chip shows the raw SQL + tool return. The backend only
// SENDS these when expose_data_ocean_internals is on, so prod builds show nothing
// even if this were set. Unset (prod) → hidden.
const SHOW_DO_INTERNALS = process.env.REACT_APP_SHOW_DATA_OCEAN_INTERNALS === 'true';

function ToolActionBlock({ data }: Props) {
  const [expanded, setExpanded] = useState(false);
  // Recolher um corpo alto não pode deixar o navegador cortar a rolagem.
  const { disclosureRef, toggleDisclosure } = useStableDisclosure(expanded, setExpanded);
  let queries = (data.queries && data.queries.length > 0)
    ? data.queries
    : (data.label ? [data.label] : []);
  if (data.tool === 'data_ocean_query') {
    const seen = new Set<string>();
    queries = queries
      .map(sanitizeDataOceanLabel)
      .filter((q) => q && !seen.has(q) && (seen.add(q), true));
  }
  const current = queries[queries.length - 1] || '';
  const hasMultiple = queries.length > 1;
  const [labelPending, labelDone] = TOOL_LABEL[data.tool] || ['Executando', 'Executou'];
  const skill = data.tool === 'skill_read' ? data.args?.[0] : undefined;
  const discovery = data.tool === 'tool_discovery' ? data.args?.[0] : undefined;
  const discoveryLabels: Record<string, string> = {
    loading: 'Buscando ferramentas', loaded: 'Ferramentas encontradas',
    empty: 'Nenhuma ferramenta encontrada', error: 'Falha ao buscar ferramentas',
  };
  // carregar_skill com `recurso` lê um arquivo da skill (ex.: exemplo.py);
  // chamá-lo de "Skill carregada" repetia a linha da skill.
  const skillLabel = skill && (skill.resource
    ? `${skill.status === 'error' ? 'Falha ao ler' : skill.status === 'loading' ? 'Lendo' : 'Leu'} ${skill.resource} da skill ${skill.skill}`
    : `${skill.status === 'error' ? 'Falha ao carregar skill' : skill.status === 'loading' ? labelPending : labelDone}: ${skill.skill}`);
  const label = discovery ? discoveryLabels[discovery.status] || labelDone : skill
    ? skillLabel
    : data.pending ? labelPending : labelDone;
  // A skill's name is already in the label; its internals (version, tool
  // count) mean nothing to the user, so the row has nothing to expand.
  // Discovery lists the tools found; its query only adds information when it
  // was a free-text search (a lookup by name would just repeat the names).
  const foundTools: string[] = discovery?.status === 'loaded' ? (discovery.tools || []) : [];
  const byName = !!discovery?.query &&
    [...discovery.query.split(',').map((n: string) => n.trim())].sort().join() === [...foundTools].sort().join();
  const detailLines = discovery?.status === 'loaded'
    ? [...(discovery.query && !byName ? [`Busca: ${discovery.query}`] : []), ...foundTools]
    : queries;
  const expandable = detailLines.length > 0 && !skill;

  return (
    <Row ref={disclosureRef} data-download-exclude>
      <Header
        $clickable={expandable}
        onClick={() => expandable && toggleDisclosure()}
      >
        <IconWrap>{TOOL_ICON[data.tool]}</IconWrap>
        <Verb $pending={!!data.pending}>{label}</Verb>
        {queries.length > 1 && <Counter>{queries.length}</Counter>}
        {expandable && (
          <ChevronWrap>
            {expanded ? (
              <ExpandLessIcon style={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 16 }} />
            )}
          </ChevronWrap>
        )}
      </Header>
      {expanded && detailLines.length > 0 && (
        <Details>
          {detailLines.map((q, i) => (
            <DetailLine key={i}>
              <DetailBullet>·</DetailBullet>
              <DetailText>{q}</DetailText>
            </DetailLine>
          ))}
        </Details>
      )}
      {expanded && SHOW_DO_INTERNALS && data.tool === 'data_ocean_query' && Array.isArray(data.args) && (
        <DebugWrap>
          {data.args.map((a: any, i: number) => (a && (a.sql || a._do_result)) ? (
            <DebugCall key={i}>
              {a.sql && (
                <>
                  <DebugLabel>SQL{a.max_rows != null ? ` · max_rows=${a.max_rows}` : ''}</DebugLabel>
                  <DebugPre>{a.sql}</DebugPre>
                </>
              )}
              {a._do_aviso && <DebugAviso>{a._do_aviso}</DebugAviso>}
              {a._do_result && (
                <>
                  <DebugLabel>retorno da tool ({a._do_result.length.toLocaleString()} chars)</DebugLabel>
                  <DebugPre>{a._do_result}</DebugPre>
                </>
              )}
            </DebugCall>
          ) : null)}
        </DebugWrap>
      )}
    </Row>
  );
}

export default ToolActionBlock;

// ============ Styled Components ============

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div<{ $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem;
  line-height: 1.2;
  align-self: flex-start;
  border-radius: 6px;
  user-select: none;
  max-width: 100%;

  &:hover {
    ${({ $clickable, theme }) =>
      $clickable
        ? `background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};`
        : ''}
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.85;
  flex-shrink: 0;
  /* Pull the icon glyph optically flush with the message text column: MUI
     SVGs carry ~3px of internal whitespace at this font size (matches
     ThinkingGroup's header alignment). */
  margin-left: -3px;
`;

const Verb = styled.span<{ $pending: boolean }>`
  font-weight: 500;
  flex-shrink: 0;
  /* Standard activity idiom: the verb shimmers while running (same effect as
     the Pensando group); the whole-row opacity pulse is gone. */
  ${({ $pending }) => ($pending ? shimmerText : css``)}
`;

const Query = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 460px;
  opacity: 0.85;
`;

const Counter = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 9px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  flex-shrink: 0;
`;

const Details = styled.div`
  margin: 2px 0 8px 22px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const DetailBullet = styled.span`
  opacity: 0.5;
  flex-shrink: 0;
`;

const DetailText = styled.span`
  word-break: break-word;
`;

// ---- Non-prod debug (SQL + tool return) ----
const DebugWrap = styled.div`
  margin: 2px 0 10px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DebugCall = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DebugLabel = styled.div`
  font-size: 0.66rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.55;
  color: ${({ theme }) => theme.font_color || '#666'};
`;

const DebugPre = styled.pre`
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.74rem;
  line-height: 1.45;
  background: ${({ theme }) => (theme as any).code_bg || '#1e1e1e'};
  color: ${({ theme }) => (theme as any).code_color || '#d4d4d4'};
  margin: 0;
  padding: 9px 11px;
  border-radius: 8px;
  overflow: auto;
  max-height: 360px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DebugAviso = styled.div`
  font-size: 0.76rem;
  color: #d29922;
  font-family: 'Fira Code', 'Consolas', monospace;
`;
